import {
  ArrowCounterClockwise,
  BellSlash,
  CalendarPlus,
  Camera,
  Check,
  PencilSimple,
  Plus,
  Ticket,
  Trash,
} from '@phosphor-icons/react';
import {
  leftLabel,
  sortVouchers,
  voucherReminder,
  type MagicVoucher,
  type Voucher,
  daysLeft,
} from '@shh/shared';
import { Button, Heading, Icon, IconButton, Sheet, Surface, Text, cn } from '@shh/ui';
import { useRef, useState, type ChangeEvent } from 'react';
import { EventForm } from '../../components/EventForm';
import { VoucherForm, readVoucherPhoto } from '../../components/leisureForms';
import { deleteVoucher, setVoucherQuiet, setVoucherUsed } from '../../data/api';
import { memberById } from '../../data/derive';
import { useLens } from '../../app/lens';
import { useFamily, type WithId } from '../../data/family';
import { Empty, useLeisureRights } from './common';
import { expired } from './FreizeitPage';
import { locale } from '../../lib/locale';
import { funText } from '../../i18n/fun';

type Editing = { voucher?: WithId<Voucher>; photo?: Promise<MagicVoucher | null> } | null;

/**
 * The vouchers from the drawer, the one that runs out first on top. The app reminds quietly on
 * "Heute" (60, 14 and 3 days before); "Einplanen" puts the visit in the calendar.
 */
export function VouchersPage() {
  const f = useFamily();
  const rights = useLeisureRights();
  const [editing, setEditing] = useState<Editing>(null);
  const [planning, setPlanning] = useState<WithId<Voucher> | null>(null);
  const camera = useRef<HTMLInputElement>(null);
  // AI only for parents – the wall with the PIN (Mike, 24.10.: "KI rechtssicher")
  const mayAi = useLens().canParent;
  const ai = Boolean(f.household?.ai?.enabled) && mayAi;
  const open = sortVouchers(f.vouchers.filter((v) => !v.used && !expired(f.today, v.until)));
  const past = f.vouchers
    .filter((v) => v.used || expired(f.today, v.until))
    .sort(
      (a, b) => (b.usedAt ?? 0) - (a.usedAt ?? 0) || (b.until ?? '').localeCompare(a.until ?? ''),
    );
  const takePhoto = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    // reading starts at once; the form shows the result when it is there
    if (file) setEditing({ photo: readVoucherPhoto(file) });
  };

  return (
    <>
      {rights.canPlan && (
        <div className="flex flex-wrap gap-gap-sm">
          {ai && (
            <>
              <Button size="lg" icon={Camera} onPress={() => camera.current?.click()}>
                {funText.photoVoucher}
              </Button>
              <input
                ref={camera}
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={takePhoto}
                aria-label={funText.voucherPhoto}
              />
            </>
          )}
          <Button
            size="lg"
            variant={ai ? 'secondary' : 'primary'}
            icon={Plus}
            onPress={() => setEditing({})}
          >
            {ai ? funText.type : funText.addVoucher}
          </Button>
        </div>
      )}

      <section aria-labelledby="open-vouchers" className="flex flex-col gap-gap-md">
        <div className="flex flex-col">
          <Heading level={2} size="md" id="open-vouchers">
            {funText.vouchersCount(open.length)}
          </Heading>
          {open.length > 1 && (
            <Text size="sm" tone="muted">
              {funText.firstOnTop}
            </Text>
          )}
        </div>
        {open.length ? (
          <ul className="grid grid-cols-1 gap-gap-md md:grid-cols-2 xl:grid-cols-3">
            {open.map((v) => (
              <li key={v.id} className="flex">
                <VoucherCard
                  voucher={v}
                  onEdit={rights.canPlan ? () => setEditing({ voucher: v }) : undefined}
                  onPlan={rights.canPlan ? () => setPlanning(v) : undefined}
                />
              </li>
            ))}
          </ul>
        ) : (
          <Empty icon={Ticket}>{rights.canPlan ? funText.noOpenPlan : funText.noOpen}</Empty>
        )}
      </section>

      {past.length > 0 && (
        <details className="group flex flex-col gap-gap-md">
          <summary className="flex min-h-touch-min cursor-pointer list-none items-center gap-gap-sm rounded-control font-bold text-text-secondary outline-none focus-visible:outline-3 focus-visible:outline-border-focus">
            {funText.past(String(past.length))}
          </summary>
          <ul className="flex flex-col gap-gap-sm pt-gap-sm">
            {past.map((v) => (
              <li key={v.id}>
                <Surface
                  tone="sunken"
                  padding="sm"
                  className="flex flex-wrap items-center gap-gap-sm"
                >
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="font-bold">{v.title}</span>
                    <span className="text-sm text-text-secondary">
                      {v.used ? funText.redeemed : funText.expired}
                      {v.value ? ` · ${v.value}` : ''}
                    </span>
                  </span>
                  {rights.canPlan && v.used && (
                    <Button
                      variant="quiet"
                      icon={ArrowCounterClockwise}
                      onPress={() => void setVoucherUsed(f.householdId, v.id, false)}
                    >
                      {funText.notRedeemed}
                    </Button>
                  )}
                  {rights.adult && (
                    <IconButton
                      icon={Trash}
                      aria-label={funText.deleteItem(v.title)}
                      onPress={() => void deleteVoucher(f.householdId, v.id)}
                    />
                  )}
                </Surface>
              </li>
            ))}
          </ul>
        </details>
      )}

      <Sheet
        isOpen={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        title={editing?.voucher ? funText.editVoucher : funText.newVoucher}
        isDismissable={false}
      >
        {editing && (
          <div className="flex flex-col gap-gap-lg">
            <VoucherForm
              voucher={editing.voucher}
              photo={editing.photo}
              onDone={() => setEditing(null)}
            />
            {editing.voucher && rights.adult && (
              <Button
                variant="quiet"
                icon={Trash}
                className="self-start"
                onPress={() => {
                  void deleteVoucher(f.householdId, editing.voucher!.id);
                  setEditing(null);
                }}
              >
                {funText.deleteVoucher}
              </Button>
            )}
          </div>
        )}
      </Sheet>
      <Sheet
        isOpen={planning !== null}
        onOpenChange={(o) => !o && setPlanning(null)}
        title={funText.plan}
        isDismissable={false}
      >
        {planning && (
          <EventForm
            draft={{
              title: planning.title,
              date: f.today,
              assignees: planning.forWhom,
              start: '18:00',
              location: planning.title,
              bring: [funText.voucherBring],
              notes:
                [
                  planning.place && funText.keptIn(planning.place),
                  planning.code && funText.codeIs(planning.code),
                ]
                  .filter(Boolean)
                  .join(' · ') || undefined,
            }}
            onDone={() => setPlanning(null)}
          />
        )}
      </Sheet>
    </>
  );
}

function VoucherCard({
  voucher: v,
  onEdit,
  onPlan,
}: {
  voucher: WithId<Voucher>;
  onEdit?: () => void;
  onPlan?: () => void;
}) {
  const f = useFamily();
  const rights = useLeisureRights();
  const reminding = voucherReminder(f.today, v) !== null;
  const left = v.until ? leftLabel(f.today, v.until, locale) : funText.noEnd;
  // the last month counts as urgent – by the number of days, not by the words
  const days = v.until ? daysLeft(f.today, v.until) : undefined;
  const urgent = days !== undefined && days >= 0 && days <= 31;
  const who =
    v.forWhom === 'all'
      ? funText.forAll
      : funText.forNames(
          v.forWhom
            .map((id) => memberById(f, id)?.name)
            .filter(Boolean)
            .join(', '),
        );
  const meta = [v.value, who, v.from && funText.from(v.from)].filter(Boolean);

  return (
    <Surface
      as="article"
      className={cn('flex w-full flex-col gap-gap-sm', urgent && 'border-feedback-warning')}
    >
      <div className="flex items-start gap-gap-sm">
        <Icon icon={Ticket} size="lg" weight="duotone" className="shrink-0 text-accent-secondary" />
        <div className="flex min-w-0 flex-1 flex-col">
          <Heading level={3} size="sm">
            {v.title}
          </Heading>
          <Text size="sm" tone="secondary">
            {meta.join(' · ')}
          </Text>
        </div>
        <span
          className={cn(
            'shrink-0 rounded-chip px-inset-xs py-0.5 text-sm font-bold',
            urgent
              ? 'bg-feedback-warning-surface text-feedback-warning'
              : 'bg-surface-sunken text-text-secondary',
          )}
        >
          {left}
        </span>
        {onEdit && (
          <IconButton
            icon={PencilSimple}
            aria-label={funText.editItem(v.title)}
            onPress={onEdit}
            className="shrink-0"
          />
        )}
      </div>
      {(v.place || v.code) && (
        <Text size="sm">
          {v.place && (
            <>
              {funText.liesIn} <strong>{v.place}</strong>
            </>
          )}
          {v.place && v.code && ' · '}
          {v.code && (
            <>
              {funText.code} <span className="font-mono font-semibold">{v.code}</span>
            </>
          )}
        </Text>
      )}
      {v.note && (
        <Text size="sm" tone="secondary">
          {v.note}
        </Text>
      )}
      {rights.canPlan && (
        <div className="mt-auto flex flex-wrap items-center gap-gap-sm pt-gap-xs">
          {onPlan && (
            <Button variant="secondary" icon={CalendarPlus} onPress={onPlan}>
              {funText.plan}
            </Button>
          )}
          <Button
            variant="quiet"
            icon={Check}
            onPress={() => void setVoucherUsed(f.householdId, v.id, true)}
          >
            {funText.used}
          </Button>
          {reminding && (
            <Button
              variant="quiet"
              icon={BellSlash}
              onPress={() => void setVoucherQuiet(f.householdId, v.id, true)}
            >
              {funText.stopReminding}
            </Button>
          )}
        </div>
      )}
    </Surface>
  );
}
